import { useEffect, useRef, useState } from "react";

interface LeafletMarker {
  addTo: (m: unknown) => LeafletMarker;
  bindPopup: (h: string) => LeafletMarker;
}

interface LeafletLike {
  map: (el: HTMLElement) => { setView: (c: [number, number], z: number) => unknown; fitBounds: (b: unknown) => unknown; remove: () => void };
  tileLayer: (url: string, opts: object) => { addTo: (m: unknown) => unknown };
  circleMarker: (c: [number, number], opts: object) => LeafletMarker;
  latLngBounds: (points: [number, number][]) => unknown;
}

/** Leaflet from the same CDN the Jinja dashboard uses, loaded on demand so the
 * bundle carries no map library for the screens that have no map. */
export interface MapSite {
  id: number;
  name: string;
  lat: number;
  lon: number;
  status: string;
  label: string;
}

// Close enough to read the streets round one chamber; `fitBounds` on a single
// point would zoom to the tile server's maximum.
const ONE_SITE_ZOOM = 16;

export function SiteMap({
  sites,
  colours,
  linkBase = "/sites",
  compact = false,
}: {
  sites: MapSite[];
  colours: Record<string, string>;
  linkBase?: string;
  /** The small map inside a site's own Location tab. */
  compact?: boolean;
}) {
  const box = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (box.current === null || sites.length === 0) {
      return;
    }
    let cancelled = false;
    let map: { remove: () => void } | null = null;
    const draw = () => {
      const L = (window as unknown as { L?: LeafletLike }).L;
      if (cancelled || box.current === null || L === undefined) {
        return;
      }
      const created = L.map(box.current);
      map = created;
      L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19, attribution: "© OpenStreetMap" }).addTo(created);
      for (const site of sites) {
        L.circleMarker([site.lat, site.lon], { radius: 6, color: colours[site.status] ?? "#333", fillOpacity: 0.8 })
          .addTo(created)
          .bindPopup(`<a href="${linkBase}/${site.id}">${site.name}</a>${site.label ? `<br>${site.label}` : ""}`);
      }
      if (sites.length === 1) {
        created.setView([sites[0].lat, sites[0].lon], ONE_SITE_ZOOM);
      } else {
        created.fitBounds(L.latLngBounds(sites.map((s) => [s.lat, s.lon] as [number, number])));
      }
    };
    if ((window as unknown as { L?: LeafletLike }).L) {
      draw();
    } else {
      const css = document.createElement("link");
      css.rel = "stylesheet";
      css.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
      document.head.append(css);
      const script = document.createElement("script");
      script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
      script.onload = draw;
      // A map library fetched from a CDN can fail -- a blocked network, an
      // offline laptop -- and a blank frame the height of the map is the worst
      // way to say so, because it reads as a map with nothing on it.
      script.onerror = () => {
        if (!cancelled) {
          setFailed(true);
        }
      };
      document.head.append(script);
    }
    return () => {
      cancelled = true;
      map?.remove();
    };
  }, [sites, colours, linkBase]);
  if (sites.length === 0) {
    return <p className="register-none">No located sites to map.</p>;
  }
  if (failed) {
    return (
      <p className="register-none">
        The map could not be loaded. {sites.length} located site{sites.length === 1 ? "" : "s"} are
        in the register either way.
      </p>
    );
  }
  return (
    <div
      ref={box}
      className={compact ? "site-map site-map-compact" : "site-map"}
      role="img"
      aria-label={sites.length === 1 ? `Map of ${sites[0].name}` : "Map of located sites"}
    />
  );
}
